import { ApiOptions, EventIdStr, Maybe, ObservationId, UserId } from '@csp/csp-common-model';
import { ElaasConsequenceCode, ElaasFormulaCodeOrIdentifier } from '@csp/csp-common-rpm-model';
import { isDefined } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import {
  AssessmentIncludeFieldV1,
  AssessmentQueryOperatorV1,
  AssessmentQueryV1,
  type AssessmentAggregateGroupResultV1,
} from '@csp/dmdp-api-assessment-dto';
import { AssessmentRestServiceV1 } from '@csp/dmdp-api-client';
import { Page, largePage, pageWithOneItem, smallPage } from '@csp/dmdp-api-common-dto';
import { first, orderBy } from 'lodash';
import { Assessment } from '../model/Assessment';
import { AssessmentAggregateGroup } from '../model/AssessmentAggregateGroup';
import { AssessmentCustomStatusType } from '../model/AssessmentCustomStatusType';
import { AssessmentId } from '../model/AssessmentId';
import { AssessmentRefKey } from '../model/AssessmentRefKey';
import { RpmAssessment } from '../model/RpmAssessment';
import { RpmAssessmentType } from '../model/RpmAssessmentType';
import { RpmAssessments } from '../model/RpmAssessments';
import { RpmAssessmentViewStatusQueryValue } from '../type/RpmAssessmentViewStatusQueryValue';
import { RpmAssessmentUtil } from '../util/RpmAssessmentUtil';
import { RpmAssessmentAggregateQuery } from './RpmAssessmentAggregateQuery';
import { RpmAssessmentQuery } from './RpmAssessmentQuery';

const orderByCustomStatusAndLatestCreatedAt = (
  groups: AssessmentAggregateGroupResultV1[],
): AssessmentAggregateGroupResultV1[] =>
  orderBy(
    groups,
    [
      (group: AssessmentAggregateGroupResultV1): Maybe<string> =>
        RpmAssessmentUtil.getStatusValueFromKey(group.fields, AssessmentCustomStatusType.VIEW_STATUS) ??
        group.fields.customStatusValue,
      (group: AssessmentAggregateGroupResultV1): Maybe<string> => group.maxAssessmentTimestamp,
    ],
    ['asc', 'desc'],
  );

const getByAssessmentId = async (assessmentId: AssessmentId, apiOptions?: ApiOptions): Promise<RpmAssessment> => {
  const rawAssessment = await AssessmentRestServiceV1.getAssessmentById(toRestOptions(apiOptions), assessmentId);
  const assessment = Assessment.from(rawAssessment);
  return RpmAssessment.from(assessment);
};

const getByEventId = async (eventId: EventIdStr, apiOptions?: ApiOptions): Promise<Maybe<RpmAssessment>> => {
  const query: AssessmentQueryV1 = {
    criterion: {
      operator: AssessmentQueryOperatorV1.AND,
      criteria: [RpmAssessmentQuery.eventIdQuery(eventId)],
    },
  };

  const { assessments = [] } = await AssessmentRestServiceV1.getAssessments(
    toRestOptions(apiOptions),
    query,
    pageWithOneItem(),
  );
  return first(assessments.map(Assessment.from).map(RpmAssessment.from));
};

const queryAggregatedAssessments = async (
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
  consequenceCodes: readonly ElaasConsequenceCode[],
  apiOptions?: ApiOptions,
): Promise<AssessmentAggregateGroup[]> => {
  const query = RpmAssessmentAggregateQuery.byFormulaCodesAndConsequencesQuery(formulaCodes, consequenceCodes);

  const { groups } = await AssessmentRestServiceV1.aggregateAssessments(toRestOptions(apiOptions), query);

  return orderByCustomStatusAndLatestCreatedAt(groups).map(AssessmentAggregateGroup.from);
};

const queryAggregatedAssessmentsByUserId = async (
  userId: UserId,
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
  consequenceCodes: readonly ElaasConsequenceCode[],
  apiOptions?: ApiOptions,
): Promise<AssessmentAggregateGroup[]> => {
  const query = RpmAssessmentAggregateQuery.byUserIdAndFormulaCodesAndConsequencesQuery(
    userId,
    formulaCodes,
    consequenceCodes,
  );

  const { groups } = await AssessmentRestServiceV1.aggregateAssessments(toRestOptions(apiOptions), query);

  return orderByCustomStatusAndLatestCreatedAt(groups).map(AssessmentAggregateGroup.from);
};

const queryAllNotViewed = async (
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
  consequenceCodes: readonly ElaasConsequenceCode[],
  page: Page,
  apiOptions?: ApiOptions,
): Promise<RpmAssessments> => {
  const query: AssessmentQueryV1 = {
    criterion: {
      operator: AssessmentQueryOperatorV1.AND,
      criteria: [
        RpmAssessmentQuery.rpmAssessmentTypesQuery(),
        RpmAssessmentQuery.algorithmCodesQuery(formulaCodes),
        RpmAssessmentQuery.algorithmResultNotOkOrConsequencesQuery(consequenceCodes),
        RpmAssessmentQuery.noViewedStatusQuery(),
      ],
    },
    sort: RpmAssessmentQuery.latestCreatedAtAndNotViewedStatusFirstSortQuery(),
  };
  const { assessments = [], paging } = await AssessmentRestServiceV1.getAssessments(
    toRestOptions(apiOptions),
    query,
    page,
  );

  return {
    assessments: assessments.map(Assessment.from).map(RpmAssessment.from),
    paging,
  };
};

const queryByObservationIds = async (
  observationIds: readonly ObservationId[],
  consequenceCodes: readonly ElaasConsequenceCode[],
  apiOptions?: ApiOptions,
): Promise<RpmAssessment[]> => {
  const query: AssessmentQueryV1 = {
    criterion: {
      operator: AssessmentQueryOperatorV1.AND,
      criteria: [
        RpmAssessmentQuery.assessmentTypesQuery(RpmAssessmentType.RPM_DEVICE_EVENT),
        RpmAssessmentQuery.algorithmResultNotOkOrConsequencesQuery(consequenceCodes),
        RpmAssessmentQuery.refIdsQuery(AssessmentRefKey.DEVICE_OBSERVATION_ID, observationIds),
      ],
    },
    sort: [RpmAssessmentQuery.latestCreatedAtSortQuery()],
  };

  const { assessments = [] } = await AssessmentRestServiceV1.getAssessments(
    toRestOptions(apiOptions),
    query,
    largePage(),
  );

  return assessments.map(Assessment.from).map(RpmAssessment.from);
};

/**
 * Query all rpm event assessments by questionnaire response ids. Optionally you can specify algorithm codes to filter
 * on.
 */
const queryByQuestionnaireResponseIds = async (
  questionnaireResponseIds: readonly string[],
  consequenceCodes: readonly ElaasConsequenceCode[],
  apiOptions?: ApiOptions,
): Promise<RpmAssessment[]> => {
  const query: AssessmentQueryV1 = {
    criterion: {
      operator: AssessmentQueryOperatorV1.AND,
      criteria: [
        RpmAssessmentQuery.assessmentTypesQuery(RpmAssessmentType.RPM_QUESTIONNAIRE_EVENT),
        RpmAssessmentQuery.algorithmResultNotOkOrConsequencesQuery(consequenceCodes),
        RpmAssessmentQuery.refIdsQuery(AssessmentRefKey.QUESTIONNAIRE_RESPONSE_ID, questionnaireResponseIds),
      ].filter(isDefined),
    },
    sort: [RpmAssessmentQuery.latestCreatedAtSortQuery()],
  };

  const { assessments = [] } = await AssessmentRestServiceV1.getAssessments(
    toRestOptions(apiOptions),
    query,
    largePage(),
  );

  return assessments.map(Assessment.from).map(RpmAssessment.from);
};

/**
 * Used to show the badge number in the patient list page. We only need to fetch 100 assessments to get the total
 * since we "99+" in the badge when there are more than 99 assessments.
 */
const queryTotalNrNotViewed = async (
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
  consequenceCodes: readonly ElaasConsequenceCode[],
  apiOptions?: ApiOptions,
): Promise<number> => {
  const query: AssessmentQueryV1 = {
    criterion: {
      operator: AssessmentQueryOperatorV1.AND,
      criteria: [
        RpmAssessmentQuery.rpmAssessmentTypesQuery(),
        RpmAssessmentQuery.noViewedStatusQuery(),
        RpmAssessmentQuery.algorithmResultNotOkOrConsequencesQuery(consequenceCodes),
        RpmAssessmentQuery.algorithmCodesQuery(formulaCodes),
      ],
    },
    fields: [
      AssessmentIncludeFieldV1.ASSESSMENT_ID,
      AssessmentIncludeFieldV1.OUTCOMES,
      AssessmentIncludeFieldV1.USER_ID,
    ],
  };

  const { assessments } = await AssessmentRestServiceV1.getAssessments(toRestOptions(apiOptions), query, smallPage());

  return assessments?.length ?? 0;
};

const queryAllByUserId = async (
  patientId: UserId,
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
  consequenceCodes: readonly ElaasConsequenceCode[],
  page: Page,
  apiOptions?: ApiOptions,
): Promise<RpmAssessments> => {
  const query: AssessmentQueryV1 = {
    criterion: {
      operator: AssessmentQueryOperatorV1.AND,
      criteria: [
        RpmAssessmentQuery.rpmAssessmentTypesQuery(),
        RpmAssessmentQuery.algorithmCodesQuery(formulaCodes),
        RpmAssessmentQuery.algorithmResultNotOkOrConsequencesQuery(consequenceCodes),
        RpmAssessmentQuery.userIdsQuery(patientId),
        RpmAssessmentQuery.allViewedStatusQuery(),
      ],
    },
    sort: RpmAssessmentQuery.latestCreatedAtAndNotViewedStatusFirstSortQuery(),
  };

  const { assessments = [], paging } = await AssessmentRestServiceV1.getAssessments(
    toRestOptions(apiOptions),
    query,
    page,
  );

  return {
    assessments: assessments.map(Assessment.from).map(RpmAssessment.from),
    paging,
  };
};

type QuerySymptomMonitoringAssessments = {
  greaterThanEventId: Maybe<EventIdStr>;
  lessThanOrEqualToEventId: Maybe<EventIdStr>;
  viewStatus: Maybe<RpmAssessmentViewStatusQueryValue>;
  specificPatientIds: Maybe<string[]>;
  apiOptions?: ApiOptions;
};
/**
 * Query all rpm event assessments with consequence codes.
 *
 * @param greaterThanEventId Optional. Only fetch assessments after this point.
 * @param lessThanOrEqualToEventId Optional. Don't fetch assessments after this point.
 * @param viewStatus Optional. Only fetch assessments with this view status. Default is NOT_VIEWED.
 * @param specificPatientIds Optional. Only fetch assessments for these patient IDs.
 */
const querySymptomMonitoringAssessments = async ({
  specificPatientIds,
  greaterThanEventId,
  lessThanOrEqualToEventId,
  viewStatus = RpmAssessmentViewStatusQueryValue.NOT_VIEWED,
  apiOptions,
}: QuerySymptomMonitoringAssessments): Promise<RpmAssessment[]> => {
  const query: AssessmentQueryV1 = {
    criterion: {
      operator: AssessmentQueryOperatorV1.AND,
      criteria: [
        RpmAssessmentQuery.assessmentTypesQuery(
          RpmAssessmentType.RPM_QUESTIONNAIRE_EVENT,
          RpmAssessmentType.RPM_DEVICE_EVENT,
        ),
        RpmAssessmentQuery.viewStatusQuery(viewStatus),
        RpmAssessmentQuery.algorithmResultNotOkOrConsequencesQuery([
          ElaasConsequenceCode.INCLUDE_IN_SYMPTOM_MONITORING_EMAIL,
        ]),
        greaterThanEventId ? RpmAssessmentQuery.eventIdGreatherThanQuery(greaterThanEventId) : undefined,
        lessThanOrEqualToEventId
          ? RpmAssessmentQuery.eventIdLessThanOrEqualToQuery(lessThanOrEqualToEventId)
          : undefined,
        specificPatientIds?.length ? RpmAssessmentQuery.userIdsQuery(...specificPatientIds) : undefined,
      ].filter(isDefined),
    },
    sort: [RpmAssessmentQuery.latestCreatedAtSortQuery()],
  };

  const { assessments = [] } = await AssessmentRestServiceV1.getAssessments(
    toRestOptions(apiOptions),
    query,
    largePage(),
  );

  return assessments.map(Assessment.from).map(RpmAssessment.from);
};

export const RpmAssessmentService = {
  getByAssessmentId,
  getByEventId,
  queryAggregatedAssessments,
  queryAggregatedAssessmentsByUserId,
  queryAllByUserId,
  queryAllNotViewed,
  queryByObservationIds,
  queryByQuestionnaireResponseIds,
  queryTotalNrNotViewed,
  querySymptomMonitoringAssessments,
};
